/**
 * Controls the cart drawer's visibility (sections/cart-drawer.liquid).
 *
 * Mirrors the filter-drawer.js approach: a body class (`cart-drawer-open`) drives the
 * slide-out, toggled from any `[js-cart-toggle]` control (e.g. the header cart icon).
 * Cart AJAX (add/remove/no-reload updates) is handled entirely by the theme's existing
 * <cart-items-component> (component-cart-items.js) inside the drawer — this file only owns
 * open/close/auto-open, the same split of responsibilities as filter-drawer + facets.js.
 *
 * Dismisses on the close button, overlay click and the Escape key. Auto-opens whenever a
 * product is added to cart anywhere on the site (PDP, quick add, etc) via the theme's
 * CartAddEvent, mirroring Horizon's own cart-drawer.js `auto-open` behaviour.
 */
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { debounce } from '@theme/utilities';

const BODY_CLASS = 'cart-drawer-open';
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let returnFocusTo = null;

function getPanel() {
  return document.querySelector('[data-cart-drawer] .cart-drawer__panel');
}

function openDrawer(event) {
  if (document.body.classList.contains(BODY_CLASS)) return;

  document.body.classList.add(BODY_CLASS);
  document.documentElement.setAttribute('scroll-lock', '');

  // Lets the upsell carousel (<cart-upsell>) lazily load recommendations the first time
  // the drawer is opened, rather than eagerly on every page view.
  document.dispatchEvent(
    new CustomEvent('cart-drawer:open', { detail: { cartUpdate: event?.type === ThemeEvents.cartUpdate } })
  );

  const panel = getPanel();
  if (!(panel instanceof HTMLElement)) return;

  const toggle = event?.target instanceof Element ? event.target.closest('[js-cart-toggle]') : null;
  const active = document.activeElement;
  if (toggle instanceof HTMLElement) {
    returnFocusTo = toggle;
  } else if (active instanceof HTMLElement && active !== document.body && !panel.contains(active)) {
    returnFocusTo = active;
  } else {
    returnFocusTo = null;
  }

  if (!(active instanceof HTMLIFrameElement)) panel.focus({ preventScroll: true });
}

function closeDrawer() {
  if (!document.body.classList.contains(BODY_CLASS)) return;

  document.body.classList.remove(BODY_CLASS);
  document.documentElement.removeAttribute('scroll-lock');

  returnFocusTo?.focus({ preventScroll: true });
  if (!returnFocusTo || document.activeElement !== returnFocusTo) {
    const toggle = Array.from(document.querySelectorAll('[js-cart-toggle]')).find(
      (element) => element instanceof HTMLElement && element.getClientRects().length > 0
    );
    if (toggle instanceof HTMLElement) toggle.focus({ preventScroll: true });
  }
  returnFocusTo = null;

  const active = document.activeElement;
  if (active instanceof HTMLElement && getPanel()?.contains(active)) active.blur();
}

function trapFocus(event) {
  const panel = getPanel();
  if (!(panel instanceof HTMLElement)) return;

  const focusables = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) => element instanceof HTMLElement && element.getClientRects().length > 0
  );
  if (focusables.length === 0) return;

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;

  if (!panel.contains(active)) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && (active === first || active === panel)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.closest('[js-cart-toggle]')) {
    event.preventDefault();
    openDrawer(event);
    return;
  }

  if (target.closest('[data-cart-drawer-close]') || target.closest('[data-cart-drawer-overlay]')) {
    event.preventDefault();
    closeDrawer();
  }
});

document.addEventListener('keydown', (event) => {
  if (!document.body.classList.contains(BODY_CLASS)) return;

  if (event.key === 'Escape') closeDrawer();
  else if (event.key === 'Tab') trapFocus(event);
});

// CartAddEvent shares its event name with CartUpdateEvent (both are ThemeEvents.cartUpdate),
// so this also fires for in-drawer quantity/remove actions — openDrawer() is a no-op when
// already open, so that's harmless, and matches how Horizon's own cart-drawer.js reacts to
// the same event without distinguishing the source either.
document.addEventListener(ThemeEvents.cartUpdate, openDrawer);

/**
 * Self-heal the drawer after rapid cart changes.
 *
 * Overlapping section renders — e.g. adding from the PDP (product-form re-renders the drawer)
 * while immediately removing an item in the open drawer — can let a stale render win, leaving
 * the drawer showing the wrong count / subtotal / line items. Once activity settles, if the
 * count the drawer is showing no longer matches the authoritative cart, force one fresh render
 * of the drawer section (the theme's own morph). No-op on normal single actions, so there's no
 * extra work or flicker in the common case.
 */
const reconcileDrawer = debounce(async () => {
  if (!document.body.classList.contains(BODY_CLASS)) return;

  const cartItems = document.querySelector('[data-cart-drawer] cart-items-component');
  const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : null;
  if (!sectionId) return;

  try {
    const root = window.Shopify?.routes?.root ?? '/';
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const shown = document.querySelector('[data-cart-drawer] [ref="cartItemCount"]')?.textContent?.trim();

    if (shown != null && String(cart.item_count) !== shown) {
      sectionRenderer.renderSection(sectionId, { cache: false });
    }
  } catch {
    // Best-effort reconciliation; ignore transient fetch failures.
  }
}, 450);

document.addEventListener(ThemeEvents.cartUpdate, reconcileDrawer);

/**
 * Keep the header cart icon in sync with the cart the same way the drawer's own count is:
 * update `.cart-item-count` and toggle `.cart-item-count-visible` on `.header-cart-icon` so
 * the badge only shows when there are items (base.css hides it otherwise).
 */
function updateHeaderCartCount(count) {
  const icon = document.querySelector('.header-cart-icon');
  if (!icon) return;
  const countEl = icon.querySelector('.cart-item-count');
  if (countEl) countEl.textContent = String(count);
  icon.classList.toggle('cart-item-count-visible', Number(count) > 0);
}

async function syncHeaderCartCount(event) {
  // Most cart events carry the fresh cart (resource); fall back to /cart.js otherwise.
  let count = event?.detail?.resource?.item_count;
  if (typeof count !== 'number') {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      count = cart.item_count;
    } catch {
      return;
    }
  }
  updateHeaderCartCount(count);
}

document.addEventListener(ThemeEvents.cartUpdate, syncHeaderCartCount);
